'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { Trash2, Radio, Flag, AlertTriangle, Share2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useSearchParams } from 'next/navigation';

interface RaceResult {
  id: string;
  event_id: string;
  position: number | null;
  position_label: string;
  car_number: string;
  driver_name: string;
  team: string;
  best_lap: string;
  total_time: string;
  gap: string;
  laps: number;
  status: string;
  nationality: string;
  category: string;
}

interface RaceEvent {
  id: string;
  name: string;
  date: string;
  venue: string;
  city: string;
  status: string;
  category: string;
  organizer_name?: string;
}

interface AvatarMap {
  [driverName: string]: string | null;
}

interface ProfileMap {
  [driverName: string]: string | null;
}

interface NationalityMap {
  [driverName: string]: string | null;
}

// ISO 3166-1 alpha-2 nationality lookup
function toISOCode(nationality: string): string | null {
  if (!nationality) return null;
  const n = nationality.trim().toLowerCase();
  const map: Record<string, string> = {
    'malaysian': 'MY', 'malaysia': 'MY',
    'singaporean': 'SG', 'singapore': 'SG',
    'indonesian': 'ID', 'indonesia': 'ID',
    'thai': 'TH', 'thailand': 'TH',
    'russian': 'RU', 'russia': 'RU',
    'british': 'GB', 'uk': 'GB', 'united kingdom': 'GB', 'england': 'GB',
    'american': 'US', 'usa': 'US', 'united states': 'US', 'us': 'US',
    'chinese': 'CN', 'china': 'CN',
    'japanese': 'JP', 'japan': 'JP',
    'korean': 'KR', 'south korea': 'KR', 'korea': 'KR',
    'australian': 'AU', 'australia': 'AU',
    'german': 'DE', 'germany': 'DE',
    'french': 'FR', 'france': 'FR',
    'italian': 'IT', 'italy': 'IT',
    'spanish': 'ES', 'spain': 'ES',
    'dutch': 'NL', 'netherlands': 'NL',
    'belgian': 'BE', 'belgium': 'BE',
    'swiss': 'CH', 'switzerland': 'CH',
    'swedish': 'SE', 'sweden': 'SE',
    'norwegian': 'NO', 'norway': 'NO',
    'danish': 'DK', 'denmark': 'DK',
    'finnish': 'FI', 'finland': 'FI',
    'polish': 'PL', 'poland': 'PL',
    'portuguese': 'PT', 'portugal': 'PT',
    'canadian': 'CA', 'canada': 'CA',
    'mexican': 'MX', 'mexico': 'MX',
    'brazilian': 'BR', 'brazil': 'BR',
    'argentinian': 'AR', 'argentina': 'AR',
    'indian': 'IN', 'india': 'IN',
    'pakistani': 'PK', 'pakistan': 'PK',
    'bangladeshi': 'BD', 'bangladesh': 'BD',
    'sri lankan': 'LK', 'sri lanka': 'LK',
    'nepali': 'NP', 'nepal': 'NP',
    'filipino': 'PH', 'philippines': 'PH',
    'vietnamese': 'VN', 'vietnam': 'VN',
    'cambodian': 'KH', 'cambodia': 'KH',
    'myanmar': 'MM', 'burmese': 'MM',
    'laotian': 'LA', 'laos': 'LA',
    'bruneian': 'BN', 'brunei': 'BN',
    'emirati': 'AE', 'uae': 'AE', 'united arab emirates': 'AE',
    'saudi': 'SA', 'saudi arabian': 'SA', 'saudi arabia': 'SA',
    'qatari': 'QA', 'qatar': 'QA',
    'kuwaiti': 'KW', 'kuwait': 'KW',
    'bahraini': 'BH', 'bahrain': 'BH',
    'omani': 'OM', 'oman': 'OM',
    'jordanian': 'JO', 'jordan': 'JO',
    'lebanese': 'LB', 'lebanon': 'LB',
    'turkish': 'TR', 'turkey': 'TR',
    'iranian': 'IR', 'iran': 'IR',
    'iraqi': 'IQ', 'iraq': 'IQ',
    'egyptian': 'EG', 'egypt': 'EG',
    'nigerian': 'NG', 'nigeria': 'NG',
    'south african': 'ZA', 'south africa': 'ZA',
    'kenyan': 'KE', 'kenya': 'KE',
    'ghanaian': 'GH', 'ghana': 'GH',
    'new zealander': 'NZ', 'new zealand': 'NZ',
    'austrian': 'AT', 'austria': 'AT',
    'czech': 'CZ', 'czech republic': 'CZ', 'czechia': 'CZ',
    'hungarian': 'HU', 'hungary': 'HU',
    'romanian': 'RO', 'romania': 'RO',
    'greek': 'GR', 'greece': 'GR',
    'ukrainian': 'UA', 'ukraine': 'UA',
    'taiwanese': 'TW', 'taiwan': 'TW',
    'hong kong': 'HK', 'hongkonger': 'HK',
    'macanese': 'MO', 'macau': 'MO',
    'mongolian': 'MN', 'mongolia': 'MN',
  };
  if (/^[a-z]{2}$/.test(n)) return n.toUpperCase();
  return map[n] || null;
}

interface CategoryGroup {
  category: string;
  results: RaceResult[];
}

function parseTimeToMs(timeStr: string): number {
  if (!timeStr) return Infinity;
  const parts = timeStr.split(':');
  if (parts.length === 2) return (parseFloat(parts[0]) * 60 + parseFloat(parts[1])) * 1000;
  if (parts.length === 3) return (parseFloat(parts[0]) * 3600 + parseFloat(parts[1]) * 60 + parseFloat(parts[2])) * 1000;
  return Infinity;
}

function msToGap(ms: number): string {
  const totalSecs = ms / 1000;
  if (totalSecs < 60) return `+${totalSecs.toFixed(3)}`;
  const mins = Math.floor(totalSecs / 60);
  const secs = (totalSecs % 60).toFixed(3);
  return `+${mins}:${secs.padStart(6, '0')}`;
}

function sortResultsByGap(rawResults: RaceResult[]): RaceResult[] {
  if (!rawResults || rawResults.length === 0) return [];
  // Sort by total_time ascending (numeric), keeping Leader/gap as display only
  const withMs = rawResults.map(r => ({
    ...r,
    _ms: parseTimeToMs(r.total_time),
  }));

  // Separate results with valid times from those without
  const withTime = withMs.filter(r => r._ms > 0 && r._ms !== Infinity);
  const withoutTime = withMs.filter(r => r._ms === 0 || r._ms === Infinity);

  // Sort by total time
  withTime.sort((a, b) => a._ms - b._ms);

  const sorted = [...withTime, ...withoutTime];
  const p1Ms = sorted[0]?._ms ?? 0;

  return sorted.map((r, idx) => ({
    ...r,
    position: idx + 1,
    position_label: String(idx + 1),
    gap: idx === 0
      ? 'Leader'
      : (r._ms > 0 && p1Ms > 0
        ? msToGap(r._ms - p1Ms)
        : r.gap),
  }));
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = { Finished: 'badge-finished', DNF: 'badge-dnf', DSQ: 'badge-dsq', DNS: 'badge-dns', Racing: 'badge-live' };
  return <span className={`px-2 py-0.5 rounded text-xs font-bold ${map[status] ?? 'badge-dns'}`}>{status}</span>;
}

function PositionCell({ position }: { position: number | null }) {
  if (!position) return <span className="font-tabular text-sm font-bold text-muted-foreground">—</span>;
  const colors: Record<number, string> = { 1: 'text-gold font-extrabold', 2: 'text-silver font-extrabold', 3: 'text-bronze font-extrabold' };
  return <span className={`font-tabular text-sm ${colors[position] ?? 'text-foreground font-bold'}`}>P{position}</span>;
}

function DriverAvatar({ name, avatarUrl }: { name: string; avatarUrl?: string | null }) {
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={`${name} profile`}
        className="w-8 h-8 rounded-full object-cover border border-border shrink-0"
      />
    );
  }
  return (
    <div className="w-8 h-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
      <span className="text-xs font-bold text-primary/60">{name?.charAt(0)?.toUpperCase() || '?'}</span>
    </div>
  );
}

function CategoryResultTable({ group, canManage, avatarMap, profileMap, nationalityMap, onDelete }: {
  group: CategoryGroup;
  canManage: boolean;
  avatarMap: AvatarMap;
  profileMap: ProfileMap;
  nationalityMap: NationalityMap;
  onDelete: (id: string, name: string) => void;
}) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-3 mb-3">
        <span className="px-3 py-1 rounded-full bg-primary/10 border border-primary/30 text-xs font-bold uppercase tracking-widest text-primary">
          {group.category || 'General'}
        </span>
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground">{group.results.length} driver{group.results.length !== 1 ? 's' : ''}</span>
      </div>
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-muted-foreground w-14">Pos</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-muted-foreground w-14">#</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Driver</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-muted-foreground hidden lg:table-cell">Team</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Best Lap</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-widest text-muted-foreground hidden md:table-cell">Total Time</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-widest text-muted-foreground hidden md:table-cell">Gap</th>
                <th className="text-center px-4 py-3 text-xs font-bold uppercase tracking-widest text-muted-foreground w-16 hidden sm:table-cell">Laps</th>
                <th className="text-center px-4 py-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Status</th>
                {canManage && <th className="text-center px-4 py-3 text-xs font-bold uppercase tracking-widest text-muted-foreground w-16">Del</th>}
              </tr>
            </thead>
            <tbody>
              {group.results.length === 0 ? (
                <tr>
                  <td colSpan={canManage ? 10 : 9} className="text-center py-10">
                    <p className="text-sm text-muted-foreground">No results for this category yet.</p>
                  </td>
                </tr>
              ) : group.results.map(result => (
                <tr key={result.id} className={`border-b border-border/50 hover:bg-muted/20 transition-colors ${result.position === 1 ? 'pos-p1' : result.position === 2 ? 'pos-p2' : result.position === 3 ? 'pos-p3' : ''} ${result.status === 'DNF' || result.status === 'DSQ' ? 'opacity-70' : ''}`}>
                  <td className="px-4 py-3.5"><PositionCell position={result.position} /></td>
                  <td className="px-4 py-3.5"><span className="text-sm font-bold font-tabular text-muted-foreground">{result.car_number}</span></td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-2">
                      <DriverAvatar name={result.driver_name} avatarUrl={avatarMap[result.driver_name]} />
                      <div>
                        {profileMap[result.driver_name] ? (
                          <a href={`/driver/${profileMap[result.driver_name]}`} className="text-sm font-semibold text-foreground hover:text-primary hover:underline break-words max-w-[160px] block">{result.driver_name}</a>
                        ) : (
                          <p className="text-sm font-semibold text-foreground break-words max-w-[160px]">{result.driver_name}</p>
                        )}
                        {nationalityMap[result.driver_name] && toISOCode(nationalityMap[result.driver_name]!) && (
                          <p className="text-xs font-bold text-muted-foreground">{toISOCode(nationalityMap[result.driver_name]!)}</p>
                        )}
                        <p className="text-xs text-muted-foreground lg:hidden">{result.team}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3.5 hidden lg:table-cell"><span className="text-sm text-muted-foreground">{result.team}</span></td>
                  <td className="px-4 py-3.5 text-right"><span className="text-sm font-bold font-tabular text-foreground">{result.best_lap}</span></td>
                  <td className="px-4 py-3.5 text-right hidden md:table-cell"><span className="text-sm font-tabular text-foreground">{result.total_time}</span></td>
                  <td className="px-4 py-3.5 text-right hidden md:table-cell"><span className={`text-sm font-tabular font-semibold ${result.gap === 'Leader' ? 'text-primary' : 'text-muted-foreground'}`}>{result.gap}</span></td>
                  <td className="px-4 py-3.5 text-center hidden sm:table-cell"><span className="text-sm font-tabular text-muted-foreground">{result.laps}</span></td>
                  <td className="px-4 py-3.5 text-center"><StatusBadge status={result.status} /></td>
                  {canManage && (
                    <td className="px-4 py-3.5 text-center">
                      <button onClick={() => onDelete(result.id, result.driver_name)} className="p-1.5 rounded text-negative hover:bg-negative/10 transition-colors"><Trash2 size={14} /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function LiveResultsClient() {
  const { isAdmin, isOrganizer } = useAuth();
  const canManage = isAdmin || isOrganizer;
  const supabase = createClient();
  const searchParams = useSearchParams();

  const [events, setEvents] = useState<RaceEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [categoryGroups, setCategoryGroups] = useState<CategoryGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<RaceEvent | null>(null);
  const [avatarMap, setAvatarMap] = useState<AvatarMap>({});
  const [profileMap, setProfileMap] = useState<ProfileMap>({});
  const [nationalityMap, setNationalityMap] = useState<NationalityMap>({});
  const [organizers, setOrganizers] = useState<string[]>([]);
  const [selectedOrganizer, setSelectedOrganizer] = useState('');
  // Cache registration data per event to avoid re-fetching on every real-time result update
  const regCacheRef = React.useRef<Record<string, { avatarMap: AvatarMap; profileMap: ProfileMap; nationalityMap: NationalityMap }>>({});

  const fetchResults = useCallback(async (eventId: string, evt?: RaceEvent | null) => {
    const { data } = await supabase.from('live_results').select('*').eq('event_id', eventId);
    if (data) {
      const allResults = data as RaceResult[];

      // Get categories from event definition first (preserves exact names like CAT 1A)
      const evtCats = (evt?.category || '').split(',').map((c: string) => c.trim()).filter(Boolean);
      // Also collect categories from actual results
      const resultCats = [...new Set(allResults.map((r: any) => r.category).filter(Boolean))];

      // Use event-defined categories as primary order; add any result cats not in event definition
      let allCats: string[];
      if (evtCats.length > 0) {
        const extraCats = resultCats.filter(rc => !evtCats.includes(rc));
        allCats = [...evtCats, ...extraCats];
      } else {
        allCats = resultCats.length > 0 ? resultCats : ['General'];
      }

      // Build per-category sorted groups — use exact category string from DB
      const groups: CategoryGroup[] = allCats.map(cat => {
        const catData = allResults.filter(r => r.category === cat || (!r.category && cat === 'General'));
        return { category: cat, results: sortResultsByGap(catData) };
      }).filter(g => g.results.length > 0);

      if (groups.length === 0 && allResults.length > 0) {
        setCategoryGroups([{ category: 'All', results: sortResultsByGap(allResults) }]);
      } else {
        setCategoryGroups(groups);
      }

      // Use cached registration data if available, otherwise fetch
      const driverNames = allResults.map(r => r.driver_name).filter(Boolean);
      if (driverNames.length > 0 && eventId) {
        if (regCacheRef.current[eventId]) {
          const cached = regCacheRef.current[eventId];
          setAvatarMap(cached.avatarMap);
          setProfileMap(cached.profileMap);
          setNationalityMap(cached.nationalityMap);
        } else {
          const { data: regs } = await supabase
            .from('event_registrations')
            .select('race_name, user_id, nationality, user_profiles(avatar_url)')
            .eq('event_id', eventId)
            .in('race_name', driverNames);

          const map: AvatarMap = {};
          const pmap: ProfileMap = {};
          const nmap: NationalityMap = {};
          if (regs) {
            regs.forEach((reg: any) => {
              if (reg.race_name) {
                pmap[reg.race_name] = reg.user_id || null;
                map[reg.race_name] = reg.user_profiles?.avatar_url || null;
                nmap[reg.race_name] = reg.nationality || null;
              }
            });
          }
          regCacheRef.current[eventId] = { avatarMap: map, profileMap: pmap, nationalityMap: nmap };
          setAvatarMap(map);
          setProfileMap(pmap);
          setNationalityMap(nmap);
        }
      } else {
        setAvatarMap({});
        setProfileMap({});
        setNationalityMap({});
      }
    }
  }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      const { data } = await supabase.from('race_events').select('id, name, date, venue, city, status, category, organizer_name').order('date', { ascending: false });
      if (data) {
        setEvents(data as any);

        // Build organizer list
        const orgNames = [...new Set((data as RaceEvent[]).map((e: any) => e.organizer_name).filter(Boolean))] as string[];
        setOrganizers(orgNames);

        const eventParam = searchParams?.get('event');
        if (eventParam && data.find((e: any) => e.id === eventParam)) {
          setSelectedEventId(eventParam);
        } else if (data.length > 0) {
          setSelectedEventId(data[0].id);
        }
      }
      setLoading(false);
    };
    fetchEvents();

    const eventsChannel = supabase
      .channel('live-events-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'race_events' }, async () => {
        const { data } = await supabase.from('race_events').select('id, name, date, venue, city, status, category, organizer_name').order('date', { ascending: false });
        if (data) setEvents(data as any);
      })
      .subscribe();

    return () => { supabase.removeChannel(eventsChannel); };
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      const evt = events.find(e => e.id === selectedEventId);
      setSelectedEvent(evt || null);
      fetchResults(selectedEventId, evt);
    }
  }, [selectedEventId, events]);

  useEffect(() => {
    if (!selectedEventId) return;
    const resultsChannel = supabase
      .channel(`live-results-${selectedEventId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'live_results', filter: `event_id=eq.${selectedEventId}` }, () => {
        const evt = events.find(e => e.id === selectedEventId);
        fetchResults(selectedEventId, evt);
      })
      .subscribe();
    return () => { supabase.removeChannel(resultsChannel); };
  }, [selectedEventId, fetchResults, events]);

  const handleDelete = async (id: string, driverName: string) => {
    const { error } = await supabase.from('live_results').delete().eq('id', id);
    if (!error) {
      const evt = events.find(e => e.id === selectedEventId);
      fetchResults(selectedEventId, evt);
      toast.success(`Result for ${driverName} removed`);
    }
  };

  const handleShare = () => {
    if (!selectedEventId) return;
    const url = `${window.location.origin}/live-results/${selectedEventId}`;
    navigator.clipboard?.writeText(url).then(() => toast.success('Shareable link copied!')).catch(() => toast.error('Could not copy link'));
  };

  const raceStatus = selectedEvent?.status ?? 'completed';
  const statusConfig: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
    live: { label: 'RACE IN PROGRESS', className: 'bg-primary/10 border-primary/30 text-primary', icon: <span className="w-2.5 h-2.5 rounded-full bg-primary live-pulse" /> },
    completed: { label: 'RACE COMPLETED', className: 'bg-positive/10 border-positive/30 text-positive', icon: <Flag size={16} /> },
    upcoming: { label: 'RACE UPCOMING', className: 'bg-warning/10 border-warning/30 text-warning', icon: <AlertTriangle size={16} /> },
  };
  const raceStatusCfg = statusConfig[raceStatus] ?? statusConfig.completed;

  // Filter events by organizer
  const filteredEvents = selectedOrganizer
    ? events.filter(e => e.organizer_name === selectedOrganizer)
    : events;

  return (
    <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-12">
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Race Results</p>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-4xl md:text-5xl font-extrabold uppercase tracking-tight text-foreground mb-1">Live Results</h1>
            <p className="text-muted-foreground text-sm">{selectedEvent ? `${selectedEvent.venue}, ${selectedEvent.city}` : 'Select an event to view results'}</p>
          </div>
          <div className="flex items-center gap-3">
            {selectedEventId && (
              <button onClick={handleShare} className="flex items-center gap-1.5 px-4 py-2 text-sm font-bold uppercase tracking-wide rounded-md btn-secondary">
                <Share2 size={14} />Share Results
              </button>
            )}
            {selectedEvent?.status === 'live' && (
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 border border-primary/30">
                <span className="w-2.5 h-2.5 rounded-full bg-primary live-pulse" />
                <span className="text-sm font-bold uppercase tracking-widest text-primary">Live Updates Active</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        {/* Organizer filter */}
        {organizers.length > 1 && (
          <div className="relative">
            <select
              value={selectedOrganizer}
              onChange={e => {
                setSelectedOrganizer(e.target.value);
                const orgEvts = e.target.value ? events.filter(ev => ev.organizer_name === e.target.value) : events;
                if (orgEvts.length > 0) setSelectedEventId(orgEvts[0].id);
              }}
              className="appearance-none pl-3 pr-8 py-2.5 rounded-md bg-input border border-border text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="">All Organizers</option>
              {organizers.map(org => <option key={org} value={org}>{org}</option>)}
            </select>
            <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>
        )}

        {/* Event filter */}
        <div className="relative flex-1 max-w-lg">
          <select
            value={selectedEventId}
            onChange={e => setSelectedEventId(e.target.value)}
            className="w-full appearance-none pl-3 pr-8 py-2.5 rounded-md bg-input border border-border text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          >
            <option value="">— Select an event —</option>
            {filteredEvents.map(e => <option key={e.id} value={e.id}>{e.name} ({e.date}){e.organizer_name ? ` · ${e.organizer_name}` : ''}</option>)}
          </select>
          <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        </div>
      </div>

      {selectedEvent && (
        <div className={`flex items-center gap-3 px-5 py-3.5 rounded-xl border mb-6 ${raceStatusCfg.className}`}>
          {raceStatusCfg.icon}
          <div className="flex-1">
            <span className="text-sm font-bold uppercase tracking-widest">{raceStatusCfg.label}</span>
            <span className="mx-3 text-current/40">·</span>
            <span className="text-sm font-semibold">{selectedEvent.name}</span>
            {selectedEvent.organizer_name && (
              <span className="ml-3 text-xs text-current/70">by {selectedEvent.organizer_name}</span>
            )}
          </div>
        </div>
      )}

      {canManage && selectedEventId && (
        <div className="flex items-center gap-2 px-4 py-2.5 mb-4 rounded-lg bg-warning/10 border border-warning/30 text-warning text-sm font-semibold">
          <Radio size={15} />
          {isAdmin ? 'Admin Mode' : 'Organizer Mode'} — Manage results from the {isAdmin ? 'Admin' : 'Organizer'} Panel
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <span className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      ) : categoryGroups.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-16 text-center">
          <Radio size={40} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-sm font-semibold text-muted-foreground">No results yet</p>
          <p className="text-xs text-muted-foreground mt-1">{selectedEventId ? 'Results will appear here once added.' : 'Select an event above to view results.'}</p>
        </div>
      ) : (
        categoryGroups.map(group => (
          <CategoryResultTable
            key={group.category}
            group={group}
            canManage={canManage}
            avatarMap={avatarMap}
            profileMap={profileMap}
            nationalityMap={nationalityMap}
            onDelete={handleDelete}
          />
        ))
      )}
    </div>
  );
}